// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient svg module decls must travel with these files: consumers (e.g. functions) compile ui-kit sources transitively without our tsconfig include
/// <reference path="./svg.d.ts" />
// packages/ui-kit/src/brand/Wordmark.tsx
// C3F v18 — reference: design/system/components/brand/Wordmark.{jsx,prompt.md}
// The SVG file is the master: never recompose, never redraw.
import { type CSSProperties, type ImgHTMLAttributes, Fragment } from "react";
import wordmarkDarkUrl from "./assets/praesto-wordmark-dark.svg";
import wordmarkLightUrl from "./assets/praesto-wordmark-light.svg";

type WordmarkTheme = "auto" | "light" | "dark";

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt"> {
  /** "auto" (default) renders both variants and CSS hides the wrong one — no JS,
   *  no flicker on theme switch. Fix a variant ONLY where the surface does NOT
   *  follow the theme (always-Night bars, footer, slides): there use "light".
   *  A fixed theme is wrong in one of the two themes everywhere else. */
  theme?: WordmarkTheme;
  height?: number;
}

const SRC: Record<"light" | "dark", string> = {
  // "dark" = Night-ink wordmark, for light surfaces; "light" = Paper-ink, for dark.
  dark: wordmarkDarkUrl,
  light: wordmarkLightUrl,
};

export function Wordmark({ theme = "auto", height = 34, style, className, ...rest }: Props) {
  if (height < 34) {
    // Below 34px the wordmark is illegible — the rule lives in the component so
    // the defect announces itself instead of waiting for an audit.
    console.warn(
      `[Wordmark] height ${height}px: sotto i 34px il logotipo non è leggibile. Usa AppIcon.`,
    );
  }
  const imgStyle: CSSProperties = { height, width: "auto", display: "block", ...style };
  const img = (variant: "light" | "dark", extraClass?: string) => (
    <img
      key={variant}
      className={[className, extraClass].filter(Boolean).join(" ") || undefined}
      src={SRC[variant]}
      alt="Praesto"
      style={imgStyle}
      {...rest}
    />
  );
  if (theme === "light") return img("light");
  if (theme === "dark") return img("dark");
  // px-wm-on-light / px-wm-on-dark come from design/system/tokens/base.css.
  return (
    <Fragment>
      {img("dark", "px-wm-on-light")}
      {img("light", "px-wm-on-dark")}
    </Fragment>
  );
}
